import { useState } from 'react'
import { Button, Card, Col, Descriptions, Result, Row, Skeleton, Space, Tag, Tooltip } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { PageHeader } from '@/shared/ui/PageHeader'
import { VenueFormModal } from '@/features/venue/VenueFormModal'
import { VenueStateAction } from '@/features/venue/VenueStateAction'
import { VenueZonesPanel } from '@/features/venue/VenueZonesPanel'
import { PortalTemplatePanel } from '@/features/venue/PortalTemplatePanel'
import { fetchVenue } from '@/features/venue/venueApi'

const STATE_COLOR: Record<string, string> = { ACTIVE: 'green', SUSPENDED: 'gold', CLOSED: 'default' }

/**
 * Ob'ekt kartochkasi: identifikatsiya talablari va saqlash muddatlari
 * (zona darajasida — `VenueZonesPanel`), asosiy ma'lumot va holat.
 *
 * "Brend sozlamalari" (portal ko'rinishi) — `PortalTemplatePanel`.
 * Identifikatsiya talablari bilan ATAYLAB birlashtirilmagan: birinchisi
 * ob'ekt darajasida va `portal.template.update` bilan, ikkinchisi zona
 * darajasida va `venue.update` bilan boshqariladi (`PortalTemplateResource`
 * javadoc'i, №137 qarori).
 */
export function VenueDetailPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const [editOpen, setEditOpen] = useState(false)

  const query = useQuery({
    queryKey: queryKeys.venues.detail(id ?? ''),
    queryFn: () => fetchVenue(id ?? ''),
    enabled: id !== undefined,
  })

  if (!id) {
    return <Result status="404" title={t('notFound.title')} subTitle={t('notFound.text')} />
  }

  if (query.isPending) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 6 }} />
      </Card>
    )
  }

  if (query.isError) {
    return (
      <Result
        status="error"
        title={t('common.error')}
        subTitle={translateError(query.error, t)}
        extra={<Link to="/venues">{t('venue.backToList')}</Link>}
      />
    )
  }

  const venue = query.data

  return (
    <>
      <PageHeader
        title={venue.name}
        description={venue.code}
        extra={
          <Space wrap>
            <VenueStateAction venue={venue} />
            <Can permission="venue.update">
              <Button onClick={() => setEditOpen(true)}>{t('common.change')}</Button>
            </Can>
          </Space>
        }
      />

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title={t('venue.basicInfo')}>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label={t('venue.kind')}>{t(`venueKind.${venue.kind}`)}</Descriptions.Item>
              <Descriptions.Item label={t('subscribers.columns.status')}>
                <Tag color={STATE_COLOR[venue.state]}>{t(`venueState.${venue.state}`)}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('venue.timezone')}>{venue.timezone}</Descriptions.Item>
              <Descriptions.Item label={t('venue.contactName')}>{venue.contactName ?? '—'}</Descriptions.Item>
              <Descriptions.Item label={t('venue.contactPhone')}>{venue.contactPhone ?? '—'}</Descriptions.Item>
              <Descriptions.Item label={t('venue.funding')}>
                {t(`venueFunding.${venue.funding}`)}
              </Descriptions.Item>
              <Descriptions.Item label={t('venue.piiRetentionDays')}>
                {t('venue.retentionDays', { count: venue.piiRetentionDays })}
              </Descriptions.Item>
              <Descriptions.Item label={t('venue.journalRetentionDays')}>
                {t('venue.retentionDays', { count: venue.journalRetentionDays })}
              </Descriptions.Item>
              <Descriptions.Item label={t('subscriber.createdAt')}>
                {venue.createdAt ? (
                  <Tooltip title={utcTooltip(venue.createdAt)}>{formatDateTime(venue.createdAt)}</Tooltip>
                ) : (
                  '—'
                )}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title={t('venue.journalLinkTitle')}>
            <Space direction="vertical">
              <Link to={`/venues/journal?venueId=${venue.id}`}>{t('venue.openJournal')}</Link>
              <Can permission="campaign.manage">
                <Link to={`/venues/campaigns?venueId=${venue.id}`}>{t('venue.openCampaigns')}</Link>
              </Can>
            </Space>
          </Card>
        </Col>

        <Col span={24}>
          <VenueZonesPanel venueId={venue.id} />
        </Col>

        <Col span={24}>
          <PortalTemplatePanel venueId={venue.id} />
        </Col>
      </Row>

      <VenueFormModal open={editOpen} venue={venue} onClose={() => setEditOpen(false)} />
    </>
  )
}
